'use client';

import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { closestFrom } from '../../../utilities/dom/closest-from';
import { MARKETING_CONSENT_GIVEN_EVENT, readMarketingConsent, readMarketingConsentInForce } from './marketing-consent';
import { MARKETING_TAG_KEYS, MARKETING_TAGS, type MarketingTagKey, type MarketingTagIds } from './marketing-tags';
import { loadPageAfresh, upholdWithdrawal, withdrawMarketingConsent } from './withdraw-marketing-consent';

const DynamicMetaPixel = dynamic(() => import('@/components/shared/meta-pixel'), {
  ssr: false,
});

const DynamicGtmScript = dynamic(() => import('@/components/shared/gtm-script'), {
  ssr: false,
});

const DynamicGoogleTagScript = dynamic(() => import('@/components/shared/google-tag-script'), {
  ssr: false,
});

const DynamicActiveCampaignScript = dynamic(() => import('@/components/shared/active-campaign-script'), {
  ssr: false,
});

const DynamicOpenAiPixel = dynamic(() => import('@/components/shared/openai-pixel'), {
  ssr: false,
});

/** Each Marketing Tag's script, given the tenant's id for it; keyed like `MARKETING_TAGS`. */
const TAG_SCRIPTS: Record<MarketingTagKey, (id: string) => React.ReactNode> = {
  'meta-pixel': (pixelId) => <DynamicMetaPixel pixelId={pixelId} />,
  gtm: (gtmId) => <DynamicGtmScript gtmId={gtmId} />,
  'google-tag': (tagId) => <DynamicGoogleTagScript tagId={tagId} />,
  'active-campaign': (accountId) => <DynamicActiveCampaignScript accountId={accountId} />,
  'openai-pixel': (pixelId) => <DynamicOpenAiPixel pixelId={pixelId} />,
};

const withoutFragment = (url: string) => url.split('#')[0];

/**
 * If the consent this page's tags loaded under has gone, drops them by
 * loading `url` (or this page) afresh, and says so. The tags loaded under
 * consent given, so that is this page's own choice: in a browser that keeps
 * no cookies it stands.
 */
const dropTagsIfConsentGone = (url?: string): boolean => {
  const inForce = readMarketingConsentInForce('given');
  if (inForce === 'given') return false;

  if (inForce === 'declined') {
    // Withdrawn in another tab: withdraw here too, so the fresh page clears.
    withdrawMarketingConsent(url);
  } else {
    // The visitor cleared their cookies: the fresh page asks again.
    loadPageAfresh(url);
  }
  return true;
};

/**
 * Loads the tenant's Marketing Tags once the visitor has given Marketing
 * Consent, and on each page load while a Withdrawal stands, clears what the
 * tags stored.
 *
 * Consent can go while this page runs: withdrawn in another tab, or cleared
 * with the visitor's cookies. This layout survives client-side navigation, so
 * the tags would keep running and record the next page. Instead, a click on a
 * same-site link then becomes a full page load before any tag sees the new
 * URL. Navigations that are not link clicks (back/forward, code-driven) are
 * caught after they happen, at the next change of path, and so is a page the
 * browser restores from its back/forward cache with the tags still loaded.
 * Nothing reloads while the visitor stays on a page, which could lose a
 * half-filled form.
 */
const TrackingConsentScripts: React.FC<MarketingTagIds> = (ids) => {
  const [hasMarketingConsent, setHasMarketingConsent] = useState(false);
  const pathname = usePathname();
  const checkedPathname = useRef(pathname);

  useEffect(() => {
    upholdWithdrawal();
    setHasMarketingConsent(readMarketingConsent() === 'given');

    const handleConsentGiven = () => {
      setHasMarketingConsent(true);
    };

    window.addEventListener(MARKETING_CONSENT_GIVEN_EVENT, handleConsentGiven);

    return () => {
      window.removeEventListener(MARKETING_CONSENT_GIVEN_EVENT, handleConsentGiven);
    };
  }, []);

  useEffect(() => {
    if (!hasMarketingConsent) return;

    // Capture phase on the window: React (and with it Next's link handler)
    // listens on the document, so this runs before any of it.
    const handleClick = (event: MouseEvent) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const link = closestFrom(event.target, 'a[href]');
      if (!(link instanceof HTMLAnchorElement)) return;
      if ((link.target && link.target !== '_self') || link.hasAttribute('download')) return;
      if (link.origin !== window.location.origin) return;
      // A link to a part of this same page (`#section`, or a bare `#`) only
      // scrolls: it loads no page, so no tag records one, and
      // `location.assign` would not load one either.
      if (link.href.includes('#') && withoutFragment(link.href) === withoutFragment(window.location.href)) return;

      if (dropTagsIfConsentGone(link.href)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    // A page restored from the back/forward cache comes back with its tags
    // loaded and no navigation for the path check below to see.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) dropTagsIfConsentGone();
    };

    window.addEventListener('click', handleClick, true);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('click', handleClick, true);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [hasMarketingConsent]);

  useEffect(() => {
    if (pathname === checkedPathname.current) return;
    checkedPathname.current = pathname;
    if (hasMarketingConsent) dropTagsIfConsentGone();
  }, [hasMarketingConsent, pathname]);

  if (!hasMarketingConsent) {
    return null;
  }

  return (
    <>
      {MARKETING_TAG_KEYS.map((key) => {
        const id = MARKETING_TAGS[key].id(ids);
        return id ? <React.Fragment key={key}>{TAG_SCRIPTS[key](id)}</React.Fragment> : null;
      })}
    </>
  );
};

export default TrackingConsentScripts;
